import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Card, Select, Loading, ErrorState, EmptyState } from "./ui";
import { listSalaryMap, saveSalaryMap, updateSalaryMap, removeSalaryMap, listAccountHeads } from "../api";
import { fmtDate } from "../lib/format";

/**
 * Split a comma-separated list of people, honouring double quotes so a name
 * containing a comma or leading space can be entered as "smith, john".
 * e.g. "edward, amanda, \"blackledge tom\"" -> ['edward','amanda','blackledge tom']
 */
const splitPeople = (input) => {
  const out = [];
  let cur = "";
  let inQ = false;
  for (const ch of String(input || "")) {
    if (ch === '"') { inQ = !inQ; continue; }
    if (ch === "," && !inQ) { out.push(cur); cur = ""; continue; }
    cur += ch;
  }
  out.push(cur);
  return out.map((s) => s.trim()).filter(Boolean);
};

/**
 * Person -> account-head mapping, used by Expenses2 to put one person's salary
 * rows (across months) on a single line. Grouping is per head and per project.
 * Works on any head (Staff Salaries, Partner Drawings, ...).
 */
export default function SalaryMap() {
  const [mappings, setMappings] = useState([]);
  const [heads, setHeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState("");

  const [newHead, setNewHead] = useState("");
  const [newPerson, setNewPerson] = useState("");
  const [saving, setSaving] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editHead, setEditHead] = useState("");
  const [editPerson, setEditPerson] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [m, h] = await Promise.all([listSalaryMap(), listAccountHeads()]);
      setMappings(m);
      setHeads(h);
      setNewHead((v) => v || h.find((x) => x.name === "Staff Salaries")?.name || h[0]?.name || "");
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const grouped = useMemo(() => {
    const byHead = new Map();
    mappings.forEach((m) => {
      if (!byHead.has(m.head)) byHead.set(m.head, []);
      byHead.get(m.head).push(m);
    });
    return [...byHead.entries()]
      .sort((a, b) => a[0].localeCompare(b[0]));
  }, [mappings]);

  const add = async () => {
    const people = splitPeople(newPerson);
    if (!newHead || !people.length) return;
    setSaving(true);
    setNotice("");
    try {
      await saveSalaryMap(newHead, people);
      setNewPerson("");
      await load();
    } catch (err) {
      setNotice(err.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (m) => {
    if (!window.confirm(
      `Remove "${m.person}" from ${m.head}?\n\nTheir rows will no longer be grouped together on Expenses2.`
    )) return;
    setNotice("");
    try {
      await removeSalaryMap(m.id);
      setMappings((prev) => prev.filter((x) => x.id !== m.id));
    } catch (err) {
      setNotice(err.message);
    }
  };

  const startEdit = (m) => {
    setEditId(m.id);
    setEditHead(m.head);
    setEditPerson(m.person);
  };

  const saveEdit = async () => {
    const person = editPerson.trim();
    if (!editHead || !person) return;
    setSaving(true);
    setNotice("");
    try {
      const { mapping } = await updateSalaryMap(editId, editHead, person);
      setMappings((prev) => prev.map((x) => (x.id === mapping.id ? mapping : x)));
      setEditId(null);
    } catch (err) {
      setNotice(err.message);
    } finally {
      setSaving(false);
    }
  };

  const cancelEdit = () => setEditId(null);

  if (loading) return <Loading label="Loading grouping map…" />;
  if (error) return <ErrorState message={error} onRetry={load} />;

  const headOptions = heads.map((h) => ({ value: h.name, label: h.name }));

  return (
    <div className="stack">
      <Card
        title="Grouping map"
        note="a person on an account head → their salary rows collapse onto one line (per month column) when several months are selected"
        flush
      >
        {notice && <div className="notice notice--warn">{notice}</div>}

        <div className="dept-add">
          <div className="field">
            <span className="field-label">Account head</span>
            <Select value={newHead} onChange={setNewHead} options={headOptions} style={{ minWidth: 200 }} />
          </div>
          <div className="field" style={{ flex: 1, minWidth: 180 }}>
            <span className="field-label">People</span>
            <input
              className="input"
              placeholder='edward, amanda, "blackledge tom"'
              value={newPerson}
              onChange={(e) => setNewPerson(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && add()}
            />
          </div>
          <button
            className="btn btn--primary"
            onClick={add}
            disabled={!newHead || !newPerson.trim() || saving}
          >
            {saving ? "Saving…" : "Add"}
          </button>
        </div>

        <p className="toolbar-note dept-hint">
          Separate several people with commas. Quote a name that contains a
          comma or a leading space, e.g.{" "}
          <span className="code-tag">"smith, john"</span>. Grouping is per
          project: the same person on two projects stays on two lines. Only
          Expenses2's multi-month view uses this map.
        </p>

        {grouped.length === 0 ? (
          <EmptyState title="No mapped people yet" hint="Add an account head and a person above." />
        ) : (
          grouped.map(([head, list]) => (
            <div key={head} className="salary-head">
              <div className="salary-head-title">{head}</div>
              <div className="tbl-wrap tbl-wrap--flush">
                <table className="tbl tbl-zebra">
                  <thead>
                    <tr>
                      <th className="col-flex">Person</th>
                      <th style={{ width: 160 }}>Last changed</th>
                      <th style={{ width: 1 }} />
                    </tr>
                  </thead>
                  <tbody>
                    {list.map((m) => {
                      const editing = editId === m.id;
                      return (
                        <tr key={m.id}>
                          <td>
                            {editing ? (
                              <input
                                className="input"
                                value={editPerson}
                                autoFocus
                                onChange={(e) => setEditPerson(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") saveEdit();
                                  if (e.key === "Escape") cancelEdit();
                                }}
                              />
                            ) : (
                              <span className="badge">{String(m.person).toUpperCase()}</span>
                            )}
                          </td>
                          <td className="muted">
                            {editing
                              ? <span className="dash" style={{ whiteSpace: "nowrap" }}>editing…</span>
                              : (m.updated_at ? fmtDate(m.updated_at) : <span className="dash">—</span>)}
                          </td>
                          <td>
                            {editing ? (
                              <div className="admin-form-row">
                                <button
                                  className="btn btn--sm btn--primary"
                                  onClick={saveEdit}
                                  disabled={!editPerson.trim() || saving}
                                >
                                  {saving ? "Saving…" : "Save"}
                                </button>
                                <button className="btn btn--sm" onClick={cancelEdit}>Cancel</button>
                              </div>
                            ) : (
                              <div className="admin-form-row">
                                <button className="btn btn--sm" onClick={() => startEdit(m)}>Edit</button>
                                <button className="btn btn--sm btn--danger" onClick={() => remove(m)}>
                                  Remove
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ))
        )}
      </Card>
    </div>
  );
}
